// Ensures `window.localStorage` is always usable.
//
// Some browser configurations expose `window.localStorage` as `null`, or make
// it throw on access, instead of providing a working Storage object:
//   - Safari with "Block All Cookies" enabled (returns null)
//   - Private / lockdown browsing modes
//   - Some embedded webviews / sandboxed iframes
//
// When that happens, any direct `localStorage.getItem(...)` call throws
// "null is not an object (evaluating 'localStorage.getItem')". Because the
// top-level AuthenticationContextProvider reads localStorage during mount,
// that single throw is caught by the root ErrorBoundary and the entire app
// renders the "An error has occurred" fallback. The same throw inside an
// event handler (such as building the `Authorization` header before firing
// the manufacturer activation request) aborts the handler before the request
// is ever sent, so to the user "nothing happens".
//
// To keep every existing `localStorage.*` call site working without touching
// dozens of files, we install an in-memory fallback whenever the real storage
// is missing or broken. Values won't persist across reloads in that mode, but
// the app stays functional instead of crashing.

function createMemoryStorage() {
  let store = {}
  return {
    getItem(key) {
      return Object.prototype.hasOwnProperty.call(store, key)
        ? store[key]
        : null
    },
    setItem(key, value) {
      store[key] = String(value)
    },
    removeItem(key) {
      delete store[key]
    },
    clear() {
      store = {}
    },
    key(index) {
      const keys = Object.keys(store)
      return index >= 0 && index < keys.length ? keys[index] : null
    },
    get length() {
      return Object.keys(store).length
    },
  }
}

function isStorageWorking(storage) {
  if (!storage) return false
  try {
    const testKey = '__origins_storage_test__'
    storage.setItem(testKey, '1')
    storage.removeItem(testKey)
    return true
  } catch (err) {
    return false
  }
}

export function installSafeLocalStorage() {
  if (typeof window === 'undefined') return

  let working = false
  try {
    working = isStorageWorking(window.localStorage)
  } catch (err) {
    // Accessing window.localStorage itself can throw in some environments.
    working = false
  }

  if (working) return

  const memoryStorage = createMemoryStorage()

  try {
    Object.defineProperty(window, 'localStorage', {
      configurable: true,
      get() {
        return memoryStorage
      },
    })
  } catch (err) {
    // As a last resort, assign directly. This may be a no-op on a read-only
    // property, but in the environments we care about the property is null and
    // configurable, so the defineProperty path above succeeds.
    try {
      window.localStorage = memoryStorage
    } catch (assignErr) {
      // Nothing more we can safely do; leave the original value in place.
    }
  }
}

export default installSafeLocalStorage
